/**
 * Pure utility to replay tooth visual state at a specific point in time.
 * 
 * Purpose:
 * - Time-travel debugging (see what tooth looked like at any moment)
 * - Historical state reconstruction (replay encounter from start to specific time)
 * - Audit trail visualization (show state progression over time)
 * - Mini-timeline state preview (hover over timeline item → show state at that moment)
 * 
 * Use cases:
 * - "What did UR6 look like at 2:30 PM?"
 * - "Show me the chart state halfway through the encounter"
 * - "Replay this tooth's history step-by-step"
 */

import type { ToothEvent } from "../types/domain";
import { resolveToothImageKey } from "./toothVisualResolver";

/**
 * Replays tooth visual state at a specific point in time.
 * 
 * @param events - Array of ToothEvent objects for a single tooth (or multiple teeth)
 * @param upTo - Optional timestamp (ISO string or Date). If provided, only events
 *               with created_at <= upTo are considered. If omitted, uses all events (latest state).
 * @returns Resolved tooth image key (same values as resolveToothImageKey)
 * 
 * @example
 * // Get current state (latest)
 * const current = replayToothAtTime(ur6Events);
 * 
 * // Get state at specific time
 * const at2pm = replayToothAtTime(ur6Events, "2026-01-28T14:00:00Z");
 * 
 * // Get state at Date
 * const atDate = replayToothAtTime(ur6Events, new Date("2026-01-28T14:00:00Z"));
 */
export function replayToothAtTime(
  events: ToothEvent[],
  upTo?: string | Date
): string {
  if (!events || events.length === 0) {
    return "normal";
  }

  // Filter events by time if upTo is provided
  let filteredEvents = events;
  if (upTo !== undefined) {
    const cutoffTime = typeof upTo === "string" ? new Date(upTo).getTime() : upTo.getTime();
    
    if (Number.isNaN(cutoffTime)) {
      // Invalid date, fallback to all events
      filteredEvents = events;
    } else {
      filteredEvents = events.filter((event) => {
        const eventTime = new Date(event.created_at).getTime();
        return !Number.isNaN(eventTime) && eventTime <= cutoffTime;
      });
    }
  }

  // Filter out undo events (they don't contribute to visual state)
  filteredEvents = filteredEvents.filter((event) => {
    const payload = event.payload as { undo?: boolean } | null;
    return payload?.undo !== true;
  });

  if (filteredEvents.length === 0) {
    return "normal";
  }

  // Extract tooth code from first event (all events should be for same tooth)
  const firstEvent = filteredEvents[0];
  const toothCode =
    firstEvent.tooth_code ||
    ((firstEvent as { tooth?: string }).tooth as string | undefined);

  if (!toothCode) {
    return "normal";
  }

  // Delegate to toothVisualResolver for final decision
  // Resolver handles precedence rules and state mapping
  return resolveToothImageKey(toothCode, filteredEvents);
}
